import React, {useCallback} from 'react';
import Box from '@mui/material/Box';
import {useNavigate, useParams} from 'react-router';
import {Concept} from 'types';
import type {Data} from 'schemaDefinition/types';
import {useLocalization} from 'localization';
import {
  useGetAgentQuery,
  useMergeAgentsMutation,
} from 'services/agent/agentApi';
import {assertAgentValue} from 'services/utils';
import {mapRtkErrorToHttpError} from 'store/api';
import {Scene, Text, useSnacks} from 'components';
import {RtkContent} from 'components/rtkContent';
import {CompareEntities} from 'widgets/compareEntities';
import {useAgentFormConfiguration} from 'editors/agent/hooks/useAgentFormConfiguration';
import {useSceneExtensionsContext} from 'sceneExtensions';

export const MergeAgentsSceneCore: React.FC = () => {
  const {t} = useLocalization();
  const {successSnack, errorSnack} = useSnacks();
  const navigate = useNavigate();
  const {currentExtension} = useSceneExtensionsContext();
  const configuration = useAgentFormConfiguration();
  const [mergeAgents] = useMergeAgentsMutation();

  const {targetId: targetIdParam, sourceId: sourceIdParam} = useParams();
  const targetId = targetIdParam || '';
  const sourceId = sourceIdParam || '';

  const {
    data: targetData,
    status: targetStatus,
    error: targetError,
  } = useGetAgentQuery({
    id: targetId,
  });
  const {
    data: sourceData,
    status: sourceStatus,
    error: sourceError,
  } = useGetAgentQuery({
    id: sourceId,
  });

  const onSave = useCallback(
    (target: Data, source: Data) => {
      return mergeAgents({
        target: assertAgentValue(target),
        source: assertAgentValue(source),
      })
        .unwrap()
        .then(() => {
          successSnack('Agentene ble slått sammen');
          navigate('/mergeOverview');
          return Promise.resolve();
        })
        .catch(error => {
          errorSnack('En feil oppstod!', mapRtkErrorToHttpError(error));
        });
    },
    [errorSnack, mergeAgents, navigate, successSnack],
  );

  return (
    <Scene
      browserTitle={t('appbar.agents')}
      rightPanelConfig={currentExtension?.panelConfig}>
      <Scene.Header title={'Agentsammenslåing'} />
      <Scene.Content>
        <RtkContent status={targetStatus} error={targetError}>
          <RtkContent status={sourceStatus} error={sourceError}>
            {targetData && sourceData ? (
              <CompareEntities
                targetData={targetData!}
                sourceData={sourceData!}
                schemaRef={{
                  entity: Concept.agent,
                  subType: targetData!.agentType,
                }}
                onSave={onSave}
                formConfiguration={configuration}
                targetHeader={
                  <Box ml={2}>
                    <Text variant="h4" textTransform="uppercase">
                      Agent som beholdes
                    </Text>
                    <Text variant="caption">
                      Denne agenten vil få alle koblingene, og eventuelle
                      endringer som gjøres.
                    </Text>
                  </Box>
                }
                sourceHeader={
                  <Box ml={2}>
                    <Text variant="h4" textTransform="uppercase">
                      Agent som slettes
                    </Text>
                    <Text variant="caption">
                      Koblingene vil bli flyttet fra denne agenten til den andre
                      agenten, og denne agenten vil bli slettet etterpå.
                    </Text>
                  </Box>
                }
              />
            ) : null}
          </RtkContent>
        </RtkContent>
      </Scene.Content>
      {currentExtension ? (
        <Scene.RightPanel>
          {currentExtension.renderRightPanel()}
        </Scene.RightPanel>
      ) : null}
    </Scene>
  );
};
